import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { IFHError } from 'src/apis/HttpBaseApiWrapper';
import { useStudentDetailsApi } from './studentDetails.api';
import {
  TStudentDetailsProfileResponse,
  TStudentDetailsAttendanceParams,
  TStudentDetailsAttendanceResponse,
  TStudentDetailsFeesResponse,
  TStudentDetailsAssessmentsParams,
  TStudentDetailsAssessmentsResponse,
  TStudentDetailsDocumentsParams,
  TStudentDetailsDocumentsResponse,
  TFeeUpdateRequest,
} from './studentDetails.interfaces';

const getStudentProfileKey = 'student-details-profile';
const getStudentAttendanceKey = 'student-details-attendance';
const getStudentFeesKey = 'student-details-fees';
const getStudentAssessmentsKey = 'student-details-assessments';
const getStudentDocumentsKey = 'student-details-documents';

export const useStudentDetailsProfileHook = (
  studentId: string,
  options?: UseQueryOptions<TStudentDetailsProfileResponse, IFHError<{ message: string }>>
): UseQueryResult<TStudentDetailsProfileResponse, IFHError<{ message: string }>> => {
  const { getStudentProfile } = useStudentDetailsApi();

  return useQuery<TStudentDetailsProfileResponse, IFHError<{ message: string }>>({
    queryKey: [getStudentProfileKey, studentId],
    queryFn: () => getStudentProfile(studentId),
    enabled: !!studentId,
    ...options,
  });
};

export const useStudentDetailsAttendanceHook = (
  studentId: string,
  params: TStudentDetailsAttendanceParams,
  options?: UseQueryOptions<TStudentDetailsAttendanceResponse, IFHError<{ message: string }>>
): UseQueryResult<TStudentDetailsAttendanceResponse, IFHError<{ message: string }>> => {
  const { getStudentAttendance } = useStudentDetailsApi();

  return useQuery<TStudentDetailsAttendanceResponse, IFHError<{ message: string }>>({
    queryKey: [getStudentAttendanceKey, studentId, params],
    queryFn: () => getStudentAttendance(studentId, params),
    enabled: !!studentId,
    ...options,
  });
};

export const useStudentDetailsFeesHook = (
  studentId: string,
  options?: UseQueryOptions<TStudentDetailsFeesResponse, IFHError<{ message: string }>>
): UseQueryResult<TStudentDetailsFeesResponse, IFHError<{ message: string }>> => {
  const { getStudentFees } = useStudentDetailsApi();

  return useQuery<TStudentDetailsFeesResponse, IFHError<{ message: string }>>({
    queryKey: [getStudentFeesKey, studentId],
    queryFn: () => getStudentFees(studentId),
    enabled: !!studentId,
    ...options,
  });
};

export const useUpdateStudentFeesMutation = () => {
  const queryClient = useQueryClient();
  const { updateStudentFees } = useStudentDetailsApi();

  return useMutation({
    mutationFn: ({ studentId, fees }: { studentId: string; fees: TFeeUpdateRequest }) =>
      updateStudentFees(studentId, fees),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [getStudentFeesKey, variables.studentId] });
    },
  });
};

export const useStudentDetailsAssessmentsHook = (
  studentId: string,
  params: TStudentDetailsAssessmentsParams,
  options?: UseQueryOptions<TStudentDetailsAssessmentsResponse, IFHError<{ message: string }>>
): UseQueryResult<TStudentDetailsAssessmentsResponse, IFHError<{ message: string }>> => {
  const { getStudentAssessments } = useStudentDetailsApi();

  return useQuery<TStudentDetailsAssessmentsResponse, IFHError<{ message: string }>>({
    queryKey: [getStudentAssessmentsKey, studentId, params],
    queryFn: () => getStudentAssessments(studentId, params),
    enabled: !!studentId,
    ...options,
  });
};

export const useStudentDetailsDocumentsHook = (
  studentId: string,
  params: TStudentDetailsDocumentsParams,
  options?: UseQueryOptions<TStudentDetailsDocumentsResponse, IFHError<{ message: string }>>
): UseQueryResult<TStudentDetailsDocumentsResponse, IFHError<{ message: string }>> => {
  const { getStudentDocuments } = useStudentDetailsApi();

  return useQuery<TStudentDetailsDocumentsResponse, IFHError<{ message: string }>>({
    queryKey: [getStudentDocumentsKey, studentId, params],
    queryFn: () => getStudentDocuments(studentId, params),
    enabled: !!studentId,
    ...options,
  });
};
